import React, { useEffect, useState } from "react";

// region External libraries
import _ from "lodash";
import Yup from "src/libraries/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@apollo/client";
import { useAtom } from "jotai";
// endregion External libraries
// region Helpers
import utils from "@helpers/utils";
import { handleErrorMessage } from "@helpers/handleErrorMessage";
// endregion Helpers
// region Hooks
import { useIdentity, PERMISSIONS } from "@hooks/useIdentity";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { GlobalMessages, RoleFormMessages } from "@languages/interfaces";
// endregion Languages
// region Atoms
import { selectedModuleIdAtom } from "@atoms/SelectIdentityModule";
import ActionDialog from "@atoms/ActionDialog";
// endregion Atoms
// region Pages
// endregion Pages
// region Store
import { ECommandResultStatus, RoleMutationsCreateArgs, RoleMutationsUpdateArgs, RoleType } from "src/store/graphql/helpers/types.graphql";
import { roleCreate, roleUpdate, TRoleCreateMutations, TRoleUpdateMutations } from "src/store/graphql/groups/role/mutation.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Interfaces
interface IFormInputs {
  name: string;
  description: string;
}

interface IFormRoleProps {
  onSave?: (role: RoleType) => void;
  onDelete?: (role: RoleType) => void;
  currentRole?: RoleType | null;
}
// endregion Interfaces

// region Constants
const formSchema = Yup.object().shape({
  name: Yup.string().required().max(100),
  description: Yup.string().optional().max(255)
});
// endregion Constants

const FormRole: React.FC<IFormRoleProps> = ({ onSave, onDelete, currentRole }) => {
  // region Mutations
  const [MutationCreateRole] = useMutation<TRoleCreateMutations, RoleMutationsCreateArgs>(
    roleCreate([
      "id", "name", "description", "userRoles.userId", "roleClaims.claimId"
    ])
  );

  const [MutationUpdateRole] = useMutation<TRoleUpdateMutations, RoleMutationsUpdateArgs>(
    roleUpdate([
      "id", "name", "description"
    ])
  );
  // endregion Mutations
  // region Hooks
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<IFormInputs>({
    resolver: yupResolver(formSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [selectedModuleId] = useAtom(selectedModuleIdAtom);
  const { validateUserAccess } = useIdentity();
  // endregion Hooks

  // region Constants
  const mode = _.isEmpty(currentRole) ? "CREATE" : "EDIT";
  // endregion Constants

  // region States
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  // endregion States

  // region Handlers
  const handleDeleteRole = () => {
    if (_.isEmpty(currentRole)) return;
    if (!hasDeletePermission) return;

    setOpenConfirmDeleteModal(true);
  };

  const confirmDeleteRole = () => {
    onDelete && currentRole && onDelete(currentRole);
    setOpenConfirmDeleteModal(false);
  };

  const handleSubmitForm: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    const createRole = async () => {
      const mutation = await MutationCreateRole({
        variables: {
          role: {
            name: data.name,
            description: data.description,
            moduleId: selectedModuleId ?? "",
            alias: _.camelCase(data.name),
            active: true
          }
        }
      });

      return mutation.data?.role.create;
    };

    const updateRole = async () => {
      const mutation = await MutationUpdateRole({
        variables: {
          roleUpdateInput: {
            id: currentRole?.id as string,
            name: data.name,
            description: data.description,
            moduleId: selectedModuleId ?? "",
            active: true
          }
        }
      });

      return mutation.data?.role.update;
    };

    try {

      setIsLoading(true);

      const response = mode === "CREATE" ? await createRole() : await updateRole();

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({ type: "success", title: response.message });

      if (onSave) onSave(response.result as RoleType);

    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });
    } finally {
      setIsLoading(false);
    }
  };
  // endregion Handlers

  useEffect(() => {
    if (currentRole) {
      setValue("name", currentRole.name);
      setValue("description", currentRole.description ?? "");
    }
  }, [currentRole]);

  useEffect(() => {
    setHasDeletePermission(validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.GROUPS.DELETE));
  }, [validateUserAccess]);

  return (
    <Styled.FormContainer
      onSubmit={(e) => utils.preventDefaultFormSubmit(e, handleSubmit(handleSubmitForm))}
    >
      <Controller
        name="name"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            autoComplete="off"
            label={t(RoleFormMessages.fieldName)}
            variant="outlined"
            error={!!formErrors.name}
            helperText={handleErrorMessage(formErrors.name, t)}
            className="default-field"
            size="small"
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            autoComplete="off"
            label={t(RoleFormMessages.fieldDescription)}
            variant="outlined"
            error={!!formErrors.description}
            helperText={handleErrorMessage(formErrors.description, t)}
            className="default-field multi-rows-field"
            multiline
            rows={3}
            size="small"
          />
        )}
      />
      <Styled.FormButtons>
        {mode === "EDIT" && (
          <Button
            text={t(GlobalMessages.buttonDelete)}
            type="button"
            className={`delete-button ${!hasDeletePermission ? "no-permission" : ""}`}
            onClick={handleDeleteRole}
            loading={isLoading}
            disabled={!hasDeletePermission}
          />
        )}
        <Button
          text={t(GlobalMessages.saveText)}
          type="submit"
          className="default-submit-button"
          loading={isLoading}
        />
      </Styled.FormButtons>
      <ActionDialog
        open={openConfirmDeleteModal}
        onClose={() => setOpenConfirmDeleteModal(false)}
        title={t(RoleFormMessages.confirmDeleteTitle)}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => { setOpenConfirmDeleteModal(false); } },
          { text: t(GlobalMessages.buttonConfirm), action: () => { confirmDeleteRole(); } }
        ]}
      >
        {t(RoleFormMessages.confirmDeleteMessage)}
      </ActionDialog>
    </Styled.FormContainer>
  );
};

FormRole.defaultProps = {
  onSave: undefined,
  onDelete: undefined,
  currentRole: null
};

export default FormRole;